import { memo, useMemo, useState, useEffect, useRef, useCallback } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useNavigate, useLocation } from "react-router-dom";
import { getColor } from "../../helpers/utils";
import Switch from '@mui/material/Switch';
import { Link } from "react-router-dom";
import Tour from "../Tour/Tour";
import InvitedUserInsightsTour from "../InvitedUserInsightsTour/InvitedUserInsightsTour";
import SegmentedButtons from "../SegmentedButtons/SegmentedButtons";
import styles from './Insights.module.css';
import LoadingModal from "../LoadingModal";
import ButtonBar from "../ButtonBar/ButtonBar";
import { useDispatch } from "react-redux";
import {
    selectShowInsightsTour,
    selectShowInvitedUserInsightsTour,
    selectShowProfileDialog,
    selectInsightsTourShown,
    selectInvitedUserInsightsTourShown,
    selectProfileDialogShown,
    setShowInvitedUserInsightsTour,
    setInsightsTourShown,
    setInvitedUserInsightsTourShown,
    setIsShowingInsightsTour,
    setIsShowingInvitedUserInsightsTour,
    setIsShowingProfileDialog,
    selectInvitationSourced
} from "../../redux/authSlice";
import { useSelector } from "react-redux";
import ScrollIndicator from "../ScrollIndicator/ScrollIndicator";

const ALL_CATEGORIES = "All Insights";

const SurveyCard = memo(({ survey, progressBarFillColor, isFlipped, surveyCardRef, takeSurveyRef, resultsRef }) => {
    return (
        <div className={`${styles.surveyCardContainer} ${isFlipped ? styles.flipped : ''}`} ref={surveyCardRef}>
            <div className={`${styles.surveyCard} ${styles.front}`}>
                {((survey.tags && survey.tags.length > 0) || survey.private) && (
                    <div className={styles.tags}>
                        {survey.private && (
                            <div className={`${styles.tag} ${styles.private}`}>Private</div>
                        )}
                        {survey.tags.map((tag, i) => (
                            <div key={i} className={styles.tag}>{tag}</div>
                        ))}
                    </div>
                )}
                <div className={styles.middle}>
                    <h4 className={styles.title}>{survey.title}</h4>
                </div>
                {survey.questions === survey.answered && (
                    <div className={`${styles.buttons} ${styles.lower}`}>
                        <div className={styles.surveyComplete} style={{ backgroundColor: progressBarFillColor }}>Complete</div>
                    </div>
                )}
            </div>
            <div className={`${styles.surveyCard} ${styles.back}`}>
                <div className={styles.middle}>
                    <p className={styles.description}>{survey.description}</p>
                </div>
                <div className={`${styles.buttons} ${styles.lower}`}>
                    <Link
                        className={styles.takeSurveyLink}
                        to={`/insights/${survey.surveyId}`}
                        ref={takeSurveyRef}
                    >{(survey.questions === survey.answered) ? "Re-take" : "Take"} survey</Link>
                    <Link
                        className={`${styles.resultsLink} ${survey.questions !== survey.answered ? styles.disabled : ''}`}
                        to={`/insights/${survey.surveyId}/results`}
                        ref={resultsRef}
                    >Results</Link>
                </div>
            </div>
        </div>
    );
});

const SurveyGroup = memo(({ surveys, progressBarFillColor = 'var(--primary-color)', surveyCardRef, takeSurveyRef, resultsRef, flipFirstCard }) => {
    return (
        <div className={styles.surveyGridContainer}>
            {
                surveys.map((survey, i) => {
                    const firstCard = (i === 0);
                    return (
                        <SurveyCard
                            key={survey.surveyId}
                            survey={survey}
                            progressBarFillColor={progressBarFillColor}
                            isFlipped={firstCard && flipFirstCard}
                            surveyCardRef={firstCard ? surveyCardRef : null}
                            takeSurveyRef={firstCard ? takeSurveyRef : null}
                            resultsRef={firstCard ? resultsRef : null}
                        />
                    );
                })
            }
        </div>
    )
});

const Insights = () => {

    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();
    const location = useLocation();
    const reduxDispatch = useDispatch();

    const toggleRef = useRef(null);
    const filterRef = useRef(null);
    const containerRef = useRef(null);
    const surveyCardRef = useRef(null);
    const takeSurveyRef = useRef(null);
    const resultsRef = useRef(null);
    const switchRef = useRef(null);

    const [surveys, setSurveys] = useState([]);
    const [insightsCategories, setInsightsCategories] = useState([]);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [buttonBarOptions, setButtonBarOptions] = useState([]);
    const [buttonBarColors, setButtonBarColors] = useState([]);
    const [categoryColors, setCategoryColors] = useState({});
    const [hideCompleted, setHideCompleted] = useState(false);

    const [tourPositionsCalculated, setTourPositionsCalculated] = useState(false);
    const [spotlightDimensions, setSpotlightDimensions] = useState({ width: 0, height: 0, left: 0, top: 0 });
    const [tourPosition, setTourPosition] = useState({ left: 0, top: 0, position: "fixed" });
    const [tourStep, setTourStep] = useState(1);
    const [showTour, setShowTour] = useState(false);
    const [showInvitedTour, setShowInvitedTour] = useState(false);
    const [showSpotlight, setShowSpotlight] = useState(false);
    const [showTourCallout, setShowTourCallout] = useState(false);
    const [flipFirstCard, setFlipFirstCard] = useState(false);

    const [selectedInsightsCategory, setSelectedInsightsCategory] = useState(ALL_CATEGORIES);

    const shouldShowInsightsTour = useSelector(selectShowInsightsTour);
    const insightsTourShown = useSelector(selectInsightsTourShown);
    const shouldShowInvitedUserInsightsTour = useSelector(selectShowInvitedUserInsightsTour);
    const invitedUserInsightsTourShown = useSelector(selectInvitedUserInsightsTourShown);
    const shouldShowProfileDialog = useSelector(selectShowProfileDialog);
    const profileDialogShown = useSelector(selectProfileDialogShown);
    const invitationSourced = useSelector(selectInvitationSourced);

    const handleInsightsCategoryChange = useCallback((insightsCategory) => {
        if (insightsCategory === selectedInsightsCategory) {
            return;
        };
        setSelectedInsightsCategory(insightsCategory);
    }, [selectedInsightsCategory]);

    const handleSegmentedControlClick = useCallback((newValue) => {
        navigate("/benchmarking");
    }, [navigate]);

    // Use a ref to track the current tour step to avoid recreating updatePositions
    const tourStepRef = useRef(tourStep);
    useEffect(() => {
        tourStepRef.current = tourStep;
    }, [tourStep]);

    const updatePositions = useCallback(() => {

        const padding = 8;
        let leftOffset, topOffset;

        if (containerRef.current) {
            const containerRect = containerRef.current.getBoundingClientRect();
            leftOffset = containerRect.left;
            topOffset = containerRect.top;
        }

        const currentStep = tourStepRef.current;

        if (toggleRef.current && (currentStep === 1)) {
            const rect = toggleRef.current.getBoundingClientRect();
            setSpotlightDimensions({ top: rect.top - padding - topOffset, left: rect.left - padding - leftOffset, width: rect.width + padding * 2, height: rect.height + padding * 2 });
            setShowSpotlight(true);
            setTourPosition({ left: rect.left - padding - leftOffset, top: rect.top + rect.height + 16 - topOffset, position: "absolute" });
            setTourPositionsCalculated(true);
            setShowTourCallout(true);
        } else if (filterRef.current && (currentStep === 2)) {
            const rect = filterRef.current.getBoundingClientRect();
            setSpotlightDimensions({ top: rect.top - padding - topOffset, left: rect.left - padding - leftOffset, width: rect.width + padding * 2, height: rect.height + padding * 2 });
            setShowSpotlight(true);
            setTourPosition({ left: rect.left - padding - leftOffset, top: rect.top + rect.height + 16 - topOffset, position: "absolute" });
            setTourPositionsCalculated(true);
            setShowTourCallout(true);
        } else if (surveyCardRef.current && (currentStep === 3)) {
            const rect = surveyCardRef.current.getBoundingClientRect();
            setSpotlightDimensions({ top: rect.top - padding - topOffset, left: rect.left - padding - leftOffset, width: rect.width + padding * 2, height: rect.height + padding * 2 });
            setShowSpotlight(true);
            setTourPosition({ left: rect.left + rect.width + padding * 2 - leftOffset, top: rect.top - padding - topOffset, position: "absolute" });
            setTourPositionsCalculated(true);
            setShowTourCallout(true);
        } else if (takeSurveyRef.current && (currentStep === 4)) {
            const rect = takeSurveyRef.current.getBoundingClientRect();
            setSpotlightDimensions({ top: rect.top - padding - topOffset, left: rect.left - padding - leftOffset, width: rect.width + padding * 2, height: rect.height + padding * 2 });
            setShowSpotlight(true);
            setTourPosition({ left: rect.left - padding - leftOffset, top: rect.top + rect.height + 16 - topOffset, position: "absolute" });
            setTourPositionsCalculated(true);
            setShowTourCallout(true);
        } else if (resultsRef.current && (currentStep === 5)) {
            const rect = resultsRef.current.getBoundingClientRect();
            setSpotlightDimensions({ top: rect.top - padding - topOffset, left: rect.left - padding - leftOffset, width: rect.width + padding * 2, height: rect.height + padding * 2 });
            setShowSpotlight(true);
            setTourPosition({ left: rect.left - padding - leftOffset, top: rect.top + rect.height + 16 - topOffset, position: "absolute" });
            setTourPositionsCalculated(true);
            setShowTourCallout(true);
        } else if (switchRef.current && (currentStep === 6)) {
            const rect = switchRef.current.getBoundingClientRect();
            setSpotlightDimensions({ top: rect.top - padding - topOffset, left: rect.left - padding - leftOffset, width: rect.width + padding * 2, height: rect.height + padding * 2 });
            setShowSpotlight(true);
            setTourPosition({ right: rect.right + padding - leftOffset, top: rect.top + rect.height + 16 - topOffset, position: "absolute" });
            setTourPositionsCalculated(true);
            setShowTourCallout(true);
        }

    }, []);

    useEffect(() => {
        if (dataLoaded && showTour) {
            const timeout = setTimeout(() => {
                requestAnimationFrame(updatePositions);
            }, 150);
            return () => clearTimeout(timeout);
        }
    }, [tourStep, dataLoaded, showTour, updatePositions]);

    useEffect(() => {
        let isMounted = true;
        const getSurveysController = new AbortController();

        const getSurveys = async () => {
            try {
                const response = await axiosPrivate.get('/insights', {
                    signal: getSurveysController.signal
                });
                if (isMounted) {
                    const surveys = structuredClone(response.data.surveys);
                    const categories = [];
                    const unique = {};
                    if (surveys.length > 0) {
                        for (const survey of surveys) {
                            if (survey.tags) {
                                survey.tags = survey.tags.split("|");
                            } else {
                                survey.tags = [];
                            }
                            if (unique[survey.insightsCategory] === undefined) {
                                unique[survey.insightsCategory] = true;
                                categories.push(survey.insightsCategory);
                            }
                        };
                        setInsightsCategories([...categories]);
                        const options = [ALL_CATEGORIES, ...categories];
                        let i = 0;
                        const colors = [];
                        const colorByCategory = {};
                        for (let option of options) {
                            const color = getColor(i);
                            colors.push(color);
                            colorByCategory[option] = color;
                            i++;
                        }
                        setButtonBarOptions(options);
                        setButtonBarColors(colors);
                        setCategoryColors(colorByCategory);
                        setSurveys([...surveys]);
                        setHideCompleted(response.data.hideCompletedInsightsSurvey);
                        reduxDispatch(setShowInvitedUserInsightsTour(response.data.showInvitedUserInsightsTour));
                        setDataLoaded(true);
                    }
                }
            } catch (err) {
                if (isMounted) {
                    console.error(err);
                    if (err.response?.status === 403) {
                        navigate('/login', { state: { from: location }, replace: true });
                    }
                }
            }
        }

        getSurveys();

        return () => {
            isMounted = false;
            getSurveysController.abort();
        }
    }, [axiosPrivate, navigate, location, reduxDispatch]);

    useEffect(() => {
        if (dataLoaded) {
            if (shouldShowInsightsTour && !insightsTourShown) {
                setShowTour(true);
                reduxDispatch(setIsShowingInsightsTour(true));
            } else if (shouldShowInvitedUserInsightsTour && !invitedUserInsightsTourShown) {
                setShowInvitedTour(true);
                reduxDispatch(setIsShowingInvitedUserInsightsTour(true));
            } else if (shouldShowProfileDialog && !profileDialogShown && !invitationSourced) {
                reduxDispatch(setIsShowingProfileDialog(true));
            }
        }
    }, [dataLoaded, shouldShowInsightsTour, insightsTourShown, shouldShowInvitedUserInsightsTour, invitedUserInsightsTourShown, shouldShowProfileDialog, profileDialogShown, reduxDispatch]);

    const toggleFilter = useCallback(async (e) => {
        const checked = e.target.checked;
        const json = { hideCompletedInsightsSurveys: checked };
        setHideCompleted(checked);

        try {
            const response = await axiosPrivate.put(`/profile`,
                JSON.stringify(json),
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true
                });
            console.log(response?.data);
        } catch (err) {
            console.error(err);
        }
    }, [axiosPrivate]);

    const nextTourStep = useCallback(() => {
        if (tourStep === 3) {
            setFlipFirstCard(true);
            setShowSpotlight(false);
            setShowTourCallout(false);
            setTimeout(() => {
                setTourStep(prevStep => Math.min(prevStep + 1, 6));
            }, 400);
        } else if (tourStep === 5) {
            setFlipFirstCard(false);
            setShowSpotlight(false);
            setShowTourCallout(false);
            setTimeout(() => {
                setTourStep(prevStep => Math.min(prevStep + 1, 6));
            }, 400);
        } else {
            setTourStep(prevStep => Math.min(prevStep + 1, 6));
        }
    }, [tourStep]);

    const previousTourStep = useCallback(() => {
        if (tourStep === 6) {
            setFlipFirstCard(true);
            setShowSpotlight(false);
            setShowTourCallout(false);
            setTimeout(() => {
                setTourStep(prevStep => Math.max(prevStep - 1, 1));
            }, 400);
        } else if (tourStep === 4) {
            setFlipFirstCard(false);
            setShowSpotlight(false);
            setShowTourCallout(false);
            setTimeout(() => {
                setTourStep(prevStep => Math.max(prevStep - 1, 1));
            }, 400);
        } else {
            setTourStep(prevStep => Math.max(prevStep - 1, 1));
        }
    }, [tourStep]);

    const closeTour = useCallback(() => {
        setFlipFirstCard(false);
        reduxDispatch(setInsightsTourShown());
        reduxDispatch(setIsShowingInsightsTour(false));
        setShowTour(false);
    }, [reduxDispatch]);

    const closeInvitedTour = useCallback(() => {
        reduxDispatch(setInvitedUserInsightsTourShown());
        reduxDispatch(setIsShowingInvitedUserInsightsTour(false));
        setShowInvitedTour(false);
    }, [reduxDispatch]);

    const filteredCategories = useMemo(() => {
        return insightsCategories.filter(category => {
            let surveysToShow = 0;
            for (let survey of surveys) {
                if (survey.insightsCategory === category) {
                    if ((survey.questions > survey.answered) || (hideCompleted === false)) {
                        surveysToShow++;
                    }
                }
            }
            if (surveysToShow) {
                return ((category === selectedInsightsCategory) || (selectedInsightsCategory === ALL_CATEGORIES))
            } else {
                return false;
            }
        });
    }, [insightsCategories, surveys, hideCompleted, selectedInsightsCategory]);

    const filteredSurveysByCategory = useMemo(() => {
        const result = {};
        filteredCategories.forEach(category => {
            result[category] = surveys.filter((survey) =>
                (survey.insightsCategory === category) &&
                ((survey.questions > survey.answered) || !hideCompleted)
            );
        });
        return result;
    }, [filteredCategories, surveys, hideCompleted]);

    if (!dataLoaded) {
        return <LoadingModal />;
    }

    return (
        <section className={styles.container} ref={containerRef}>
            <ScrollIndicator />
            <SegmentedButtons labels={["Benchmarking", "Insights"]} selectedValue={"Insights"} width={600} onChange={handleSegmentedControlClick} toggleRef={toggleRef} />
            <div className={styles.toolbar}>
                <ButtonBar labels={buttonBarOptions} selectedLabel={selectedInsightsCategory} colors={buttonBarColors} onClick={handleInsightsCategoryChange} filterRef={filterRef} />
                <div className={styles.filterContainer} ref={switchRef}>
                    <label>Hide completed:</label>
                    <Switch checked={hideCompleted} onChange={toggleFilter} />
                </div>
            </div>
            <p>Take these brief surveys to benchmark yourself against peers and get our recommendations to improve your grade – emailed right to you in minutes. We add new surveys regularly.</p>
            <div className={styles.itemsColumn}>
                {
                    filteredCategories.map((category, i) => {
                        return (
                            <div key={category} className={styles.typeGroup} >
                                <h2 className={styles.typeTitle}>{category}</h2>
                                <SurveyGroup
                                    surveys={filteredSurveysByCategory[category]}
                                    progressBarFillColor={categoryColors[category]}
                                    {...(i === 0 ? {
                                        surveyCardRef: surveyCardRef,
                                        takeSurveyRef: takeSurveyRef,
                                        resultsRef: resultsRef,
                                        flipFirstCard: flipFirstCard
                                    } : {})}
                                />
                            </div>
                        )
                    })
                }
            </div>
            {
                (tourPositionsCalculated && showTour) &&
                <Tour
                    section="Insights"
                    step={tourStep}
                    steps={6}
                    onPrev={previousTourStep}
                    onNext={nextTourStep}
                    onClose={closeTour}
                    top={tourPosition.top}
                    left={tourPosition.left}
                    right={tourPosition.right}
                    position={tourPosition.position}
                    showSpotlight={showSpotlight}
                    spotlightDimensions={spotlightDimensions}
                    showCallout={showTourCallout} />
            }
            {showInvitedTour && <InvitedUserInsightsTour onClose={closeInvitedTour} />}
        </section>
    );
};

export default Insights;